import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import type { Comment } from "../types/comment";

interface UseCommentsResult {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  addComment: (body: string, parentCommentId?: string) => Promise<void>;
  removeComment: (commentId: string) => Promise<void>;
  toggleCommentLike: (commentId: string, nextLiked: boolean) => Promise<void>;
}

/**
 * Hook for managing comment state with cursor-based pagination
 *
 * Features:
 * - Fetches comments when postId is provided
 * - Supports infinite scroll via loadMore
 * - Supports adding top-level and reply comments
 * - Supports removing comments from top-level or nested replies
 *
 * @param postId - The post to load comments for, or null to skip fetching
 * @returns Comment state and control functions
 */
export function useComments(postId: string | null): UseCommentsResult {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  /**
   * Fetches comments from the API
   * @param cursorValue - Optional cursor for pagination
   * @param append - Whether to append results or replace
   */
  const fetchComments = useCallback(
    async (cursorValue: string | null, append: boolean) => {
      if (!postId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await api.social.getComments(
          postId,
          cursorValue || undefined
        );

        if (append) {
          setComments((prev) => {
            const existingIds = new Set(prev.map((c) => c.commentId));
            const newComments = response.comments.filter(
              (c) => !existingIds.has(c.commentId)
            );
            return [...prev, ...newComments];
          });
        } else {
          setComments(response.comments);
        }

        setCursor(response.cursor);
        setHasMore(response.hasMore);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load comments";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [postId]
  );

  /**
   * Load more comments using the current cursor
   * Guarded by hasMore and loading state
   */
  const loadMore = useCallback(() => {
    if (!hasMore || loading) {
      return;
    }
    fetchComments(cursor, true);
  }, [hasMore, loading, cursor, fetchComments]);

  /**
   * Add a new comment (top-level or reply)
   * @param body - Comment text
   * @param parentCommentId - Optional parent for replies
   */
  const addComment = useCallback(
    async (body: string, parentCommentId?: string) => {
      if (!postId) return;

      const response = await api.social.createComment(
        postId,
        body,
        parentCommentId
      );
      const newComment = response.comment;

      if (!parentCommentId) {
        // Top-level comment: append to end (chronological order)
        setComments((prev) => [...prev, newComment]);
      } else {
        // Reply: find parent and append to its replies array
        setComments((prev) =>
          prev.map((c) => {
            if (c.commentId === parentCommentId) {
              return {
                ...c,
                replies: [...c.replies, newComment],
                replyCount: c.replyCount + 1,
              };
            }
            return c;
          })
        );
      }
    },
    [postId]
  );

  /**
   * Remove a comment from state (top-level or nested reply)
   * @param commentId - The comment to remove
   */
  const removeComment = useCallback(
    async (commentId: string) => {
      if (!postId) return;

      await api.social.deleteComment(postId, commentId);

      setComments((prev) => {
        // Check if it's a top-level comment
        if (prev.some((c) => c.commentId === commentId)) {
          return prev.filter((c) => c.commentId !== commentId);
        }

        // Otherwise, remove from nested replies
        return prev.map((c) => {
          if (c.replies.some((r) => r.commentId === commentId)) {
            return {
              ...c,
              replies: c.replies.filter((r) => r.commentId !== commentId),
              replyCount: c.replyCount - 1,
            };
          }
          return c;
        });
      });
    },
    [postId]
  );

  /**
   * Toggle like on a comment (optimistic update + API call)
   * @param commentId - The comment to like/unlike
   * @param nextLiked - The desired like state
   */
  const toggleCommentLike = useCallback(
    async (commentId: string, nextLiked: boolean) => {
      if (!postId) return;

      // Helper to update likedByMe/likeCount for a comment anywhere in the tree
      const updateLike = (liked: boolean, delta: number) => {
        setComments((prev) =>
          prev.map((c) => {
            if (c.commentId === commentId) {
              return { ...c, likedByMe: liked, likeCount: c.likeCount + delta };
            }
            if (c.replies.some((r) => r.commentId === commentId)) {
              return {
                ...c,
                replies: c.replies.map((r) =>
                  r.commentId === commentId
                    ? { ...r, likedByMe: liked, likeCount: r.likeCount + delta }
                    : r
                ),
              };
            }
            return c;
          })
        );
      };

      // Optimistic update
      updateLike(nextLiked, nextLiked ? 1 : -1);

      try {
        if (nextLiked) {
          await api.social.likeComment(postId, commentId);
        } else {
          await api.social.unlikeComment(postId, commentId);
        }
      } catch (err) {
        // Revert on failure
        updateLike(!nextLiked, nextLiked ? -1 : 1);
        console.error("Comment like toggle error:", err);
      }
    },
    [postId]
  );

  // Reset state and fetch when postId changes
  useEffect(() => {
    if (!postId) {
      setComments([]);
      setCursor(null);
      setHasMore(false);
      setError(null);
      return;
    }

    setComments([]);
    setCursor(null);
    setHasMore(false);
    fetchComments(null, false);
  }, [postId, fetchComments]);

  return {
    comments,
    loading,
    error,
    hasMore,
    loadMore,
    addComment,
    removeComment,
    toggleCommentLike,
  };
}
